import { Injectable, inject, NgZone } from '@angular/core';
import { Subscription } from 'rxjs';

import { DepositService } from '~/core/services/deposit.service';
import { depositStatusEnum } from '~/core/enum/depositStatusEnum';
import { StorageService } from '~/core/services/storage.service';
import { AuthenticationService } from '~/core/services/authentication.service';

interface DepositPolling {
  depositId: string;
  interval: NodeJS.Timeout;
  attempts: number;
}

@Injectable({
  providedIn: 'root',
})
export class DepositPollingService {
  protected depositService = inject(DepositService);
  protected auth = inject(AuthenticationService);
  private zone = inject(NgZone);

  private readonly maxPollingAttempts = 3;
  private depositPolling: DepositPolling[] = [];
  private isLoggedInSubscription: Subscription;

  constructor() {}

  private _start(depositId: string, attemptsCount = 0) {
    this.zone.runOutsideAngular(() => {
      const newInterval = setInterval(() => {
        const polling = this.depositPolling.find((p) => p.depositId === depositId);
        if (!polling) return;

        polling.attempts++;
        this.updateCachedTracking();

        if (polling?.attempts === this.maxPollingAttempts) {
          this._stop(polling);
        }

        this.depositService.getById(depositId).subscribe(
          (result) => {
            const interval = polling?.interval;
            if (interval && result.status === depositStatusEnum.PAID) {
              this._stop(polling);
              this.depositService.trackPaidDeposit(depositId, result.value);
            } else if (interval && result.status === depositStatusEnum.REFUSED) {
              this._stop(polling);
              this.depositService.trackRefusedDeposit(depositId, result.value);
            }
          },
          () => {
            console.error('error validating deposit payment');
          },
        );
      }, 120000); // 2 minutes
      this.depositPolling.push({ depositId, interval: newInterval, attempts: attemptsCount });
    });
  }

  start(depositId: string) {
    this._start(depositId);
    this.updateCachedTracking();
  }

  private _stop(polling: DepositPolling) {
    clearInterval(polling.interval);
    const index = this.depositPolling.findIndex((p) => p.depositId === polling.depositId);

    this.depositPolling.splice(index, 1);
    this.updateCachedTracking();
    this.auth.refreshBalance(true);
  }

  private updateCachedTracking() {
    StorageService.depositsTracking = this.depositPolling.map((p) => ({
      depositId: p.depositId,
      attempts: p.attempts,
      userId: this.auth.getCurrentUser()?._id,
    }));
  }

  startCachedTracking() {
    if (!this.depositPolling.length && this.auth.isLoggedIn()) {
      const depositsTracking = StorageService.depositsTracking;
      for (const tracking of depositsTracking) {
        if (this.auth.getCurrentUser()?._id === tracking.userId) {
          this._start(tracking.depositId, tracking.attempts);
        }
      }
    } else if (!this.isLoggedInSubscription && !this.auth.isLoggedIn()) {
      this.isLoggedInSubscription = this.auth.isLoggedInObservable.subscribe((isLoggedIn) => {
        if (isLoggedIn) {
          this.startCachedTracking();
          this.isLoggedInSubscription?.unsubscribe();
        }
      });
    }
  }
}
